export const WHY_PHILOSOPHY = [
  {
    heading: "We start with the research, not the recipe.",
    body:
      "Most women's supplements are built around a familiar list of ingredients dosed at whatever fits conveniently in a capsule. We built Femora Life the other way around: we started with the clinically studied dose for each ingredient, and only then figured out the right format to deliver it — capsule, powder sachet, or softgel — rather than shrinking the dose to fit a format that's cheaper to manufacture.",
  },
  {
    heading: "That's why some products are powders, not pills.",
    body:
      "Myo-Inositol, for example, has real published evidence for supporting PCOS and fertility — but only at 2,000–4,000mg a day. That amount simply does not fit inside a swallowable capsule. Rather than include a token, under-dosed amount just to put \"Inositol\" on a capsule label, we built PCOS and Fertility as powder sachets so you get the dose the research actually used.",
  },
  {
    heading: "You deserve to know why, not just what.",
    body:
      "Every ingredient on every Femora Life label is there for a specific, documented reason — not because it's trendy, not because a competitor has it, and not as filler. Below, we walk through the reasoning behind each product.",
  },
];

export const WHY_PRODUCTS = [
  {
    slug: "pcos",
    title: "PCOS",
    concern: "Hormonal Balance & PCOS Support",
    accent: "#3E7C74",
    body:
      "PCOS affects how your body manages insulin, hormones, and inflammation — so we built this formula around the two ingredients with the strongest published evidence for that picture: Myo-Inositol + D-Chiro-Inositol (the full 4,000mg/day studied for insulin sensitivity and ovulatory support) and N-Acetyl Cysteine (NAC). We layered in Chromium, Zinc, and Vitamin D — nutrients where PCOS is commonly associated with lower levels — rather than a generic multivitamin blend.",
    note: "Why a powder, not a pill: at a clinically meaningful dose, Inositol alone is larger than a swallowable capsule can hold. A powder sachet lets us deliver the full research-backed amount, twice daily.",
  },
  {
    slug: "fertility",
    title: "Fertility",
    concern: "Reproductive Health & Fertility Support",
    accent: "#7C6BB0",
    body:
      "Fertility shares PCOS's Inositol + NAC foundation (many women navigating fertility are also managing PCOS-related insulin sensitivity), but adds what a preconception body specifically needs: CoQ10 for cellular energy relevant to egg quality, Folate, Iodine, and Choline at levels appropriate for someone actively trying to conceive, and a modest Iron dose to support what pregnancy will soon demand. A complete, standalone formula — not something that only works alongside another product.",
    note: null,
  },
  {
    slug: "pregnancy",
    title: "Pregnancy",
    concern: "Prenatal Nutrition & Mom Support",
    accent: "#D98863",
    body:
      "Pregnancy changes your nutrient needs in specific, measurable ways — blood volume increases roughly 50%, so we dose accordingly rather than using a generic adult dose. Choline is set close to the pregnancy target because it's one of the most commonly under-dosed nutrients in prenatal products today, and plays a direct role in your baby's brain development. Folate, Iodine, and DHA are included at levels reflecting their specific role in fetal development.",
    note: "Pregnancy is intentionally paired with our dedicated Iron+ for iron support — a deliberate choice we explain under Iron+.",
  },
  {
    slug: "menopause",
    title: "Menopause",
    concern: "Hormonal Balance & Menopause Support",
    accent: "#8E7CC3",
    body:
      "Hot flashes and mood changes come from different underlying mechanisms, so we address both. Black Cohosh carries the strongest published evidence for vasomotor symptom relief (hot flashes, night sweats). Magnolia Bark targets the mood and irritability side — a distinct complaint often overlooked in single-ingredient menopause products. We layered in Calcium, Vitamin D, and Magnesium because bone density loss accelerates during this life stage.",
    note: null,
  },
  {
    slug: "postpartum",
    title: "Postpartum",
    concern: "Postpartum Recovery & New Mom Support",
    accent: "#D17BA0",
    body:
      "Recovery after delivery has different priorities than pregnancy itself. Choline is dosed at the full lactation target — meaningfully higher than our own Pregnancy formula — because it directly supports both your recovery and, if you're nursing, your baby's development through breast milk. We included Iron, Calcium, and B12 at levels that reflect real postpartum depletion, not just general maintenance.",
    note: null,
  },
  {
    slug: "vitality50",
    title: "Vitality50+",
    concern: "Healthy Aging & Vitality Support (Age 50+)",
    accent: "#5B9A8B",
    body:
      "Fifty and beyond isn't just \"menopause continued\" — it's a shift toward long-term maintenance: bones, joints, heart, and cognitive health. That's why this formula includes CoQ10 (cellular energy, which naturally declines with age), Curcumin (joint comfort), Glucosamine (joint structure), and Lutein/Zeaxanthin (eye health) — ingredients most basic 50+ multivitamins skip in favor of just topping up vitamin levels.",
    note: "This is our only two-tablet-a-day product, and that's by design — Glucosamine alone requires more room than a single tablet can hold at a meaningful dose. We split the dose rather than under-dose a differentiating ingredient.",
  },
  {
    slug: "balance",
    title: "Balance",
    concern: "Hormonal Balance & Well-being Support",
    accent: "#5C7AA8",
    body:
      "For the everyday hormonal ups and downs that don't rise to a diagnosed condition, we built Balance around two adaptogens with different jobs: Ashwagandha for stress and cortisol balance, and Chasteberry, traditionally used to support menstrual cycle regularity. This is a gentler, broader-audience formula than PCOS or Fertility — not meant to replace either if you have a diagnosed condition.",
    note: null,
  },
  {
    slug: "radiance",
    title: "Radiance",
    concern: "Radiant Skin, Hair & Nail Support",
    accent: "#D46A82",
    body:
      "Collagen only works at a real dose — most capsule-based \"beauty\" supplements deliver 500mg or less, well below the 2,500–10,000mg range used in actual skin-benefit research. We built Radiance as a powder specifically so we could deliver 5,000mg of Marine Collagen — a genuinely meaningful dose — alongside Hyaluronic Acid, Biotin, and Vitamin C for the full picture of skin, hair, and nail health.",
    note: null,
  },
  {
    slug: "iron-plus",
    title: "Iron+",
    concern: "Iron with Vitamins for Daily Vitality",
    accent: "#E0913E",
    body:
      "We made a deliberate structural choice: rather than spreading iron across multiple products (which risks a customer unknowingly stacking more iron than is safe), we consolidated it into one dedicated formula. Vitamin C is included specifically because it significantly improves how well your body absorbs iron — an easy detail some iron supplements skip.",
    note: null,
  },
  {
    slug: "sleep",
    title: "Sleep",
    concern: "Restful Sleep & Relaxation Support",
    accent: "#3F5488",
    body:
      "Sleep support isn't one mechanism — it's several working together. Melatonin signals your body's clock. Magnesium, L-Theanine, Chamomile, Valerian, and GABA each support a different part of the relaxation response. We use a moderate, non-habit-forming dose of Melatonin rather than an aggressive one, because a gentler nightly dose is more appropriate for regular use.",
    note: null,
  },
  {
    slug: "gut",
    title: "Gut",
    concern: "Gut Health & Digestive Support",
    accent: "#6E9A4F",
    body:
      "Real gut support requires real amounts of fiber and targeted amino acids — not trace amounts. We dosed Prebiotic Fiber and L-Glutamine at 5,000mg each, in the range actually used in gut-health research, which is only possible in a powder format. We intentionally left Peppermint Oil out: it needs an intact enteric coating to work properly, which doesn't survive being dissolved in water — so it should come from a separate capsule, not a compromised version blended into a drink.",
    note: null,
  },
  {
    slug: "daily",
    title: "Daily",
    concern: "Essential Daily Multivitamin for Women",
    accent: "#C1567A",
    body:
      "Daily is built to be the safe, foundational layer under any of our other products — which is why every nutrient is dosed at a general maintenance level, not a therapeutic one. This is intentional: it means you can pair Daily with PCOS, Menopause, or any other Femora Life product without doubling up on high-dose actives.",
    note: null,
  },
  {
    slug: null,
    title: "Omega-3",
    concern: "Standalone DHA/EPA Support",
    accent: "#2F7D74",
    body:
      "Some of our formulas don't include DHA — not because we don't believe in it, but because liquid oil physically cannot be compressed into a tablet alongside dry powders like calcium or curcumin. Rather than force a compromise, we built a dedicated Omega-3 softgel (500mg algal DHA/EPA, plant-sourced) as the companion product for Vitality50+, Postpartum, and Balance — or simply as a standalone Omega-3 for anyone who wants it.",
    note: null,
  },
];

export const WHY_CLOSING = {
  heading: "A note on what we won't do",
  body:
    "We won't include an ingredient just because a competitor has it, and we won't quietly under-dose an active ingredient to fit a cheaper format. If a dose doesn't fit a capsule, we build a powder. If two ingredients shouldn't be combined for safety reasons, we say so on the label rather than leaving you to find out the hard way.",
  disclaimer:
    "This page reflects the current formulation of each Femora Life product. Doses are based on published research ranges and official nutritional guidelines; individual needs vary, so we always recommend talking to your doctor about what's right for you — especially if you're pregnant, nursing, or taking medication.",
};
